<template>
  <form
    id="pillar-group-form"
    ref="pillar-group-form"
    class="tw-max-w-full tw-mx-auto tw-flex-shrink-0 tw-mb-20 auth-card tw-flex tw-flex-wrap"
    method="post"
    action="/survey-type/create"
    @submit.prevent="onSubmit"
  >

    <div class="tw-w-full tw-mb-4">
      <h1 class="tw-text-4xl font-title">{{ surveyTypeId ? 'Edit Survey Context' : 'Create a Survey Context' }}</h1>
      <!-- <h6 class="tw-text-base opacity-78 tw-text-app-deep-blue">Please fill all inputs</h6> -->
    </div>

    <div class="tw-w-full tw-mb-4 tw-flex-shrink-0 tw-px-1" v-if="!surveyTypeId">
      <SelectGroup
        label="Survey Context"
        name="type"
        nameKey="name"
        placeholder="Survey Context"
        input-classes="my-select--huge tw-font-sans tw-bg-gray-200 tw-text-left"
        v-model="form.type"
        :options="unselectedTypes"
        :reduce="type => type.id"
        :selected="form.type"
        :clearable="false"
        :error="errors.type"
      />
    </div>
    <div v-else>
      <h3 class="tw-text-lg tw-mb-2 tw-font-bold">{{selectedType?.name}}</h3>
    </div>
    <div class="tw-w-full tw-mb-4 tw-flex-shrink-0 tw-px-1">
      <QuillTextareaGroup
        label="Description"
        instruct="Give a message about the survey type. This will be displayed to the user when they view the survey."
        name="description"
        type="text"
        placeholder="description"
        input-classes="tw-p-3 focus:tw-bg-gray-100"
        v-model="form.description"
        :rows="4"
        :error="errors.description"
      />
    </div>

    <div class="tw-w-full tw-flex-shrink-0 tw-px-1 tw-mb-4 tw-pt-10">
      <BaseButton
        type="submit"
        :class="[
          'tw-block tw-w-full md:tw-inline-block md:tw-w-auto tw-mb-4 tw-py-3 tw-px-8',
          `${btnClass}`
        ]"
        :text="btnText"
        :disabled="!canSubmit || btnDisabled"
      />
    </div>

  </form>
</template>

<script>
import _debounce from 'lodash/debounce';

import SelectGroup from '@/components/SelectGroup.vue';
import QuillTextareaGroup from '@/components/QuillTextareaGroup.vue';

import form from '@/modules/formHelper';

export default {
  name: 'EditSurvey',
  components: {
    QuillTextareaGroup,
    SelectGroup,
  },
  props: {
    company: {
      type: Object,
      required: true,
    },
    companyProgram: {
      type: Object,
      required: true,
    },
    surveyId: {
      type: [Number, String],
      required: true,
    },
    surveyTypeId: {
      type: [Number, String],
      default: undefined,
    },
    btnClass: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loadingForm: true,
      errorForm: undefined,
      btnText: 'Save',
      btnDisabled: false,
      types: [],
      form: {
        type: undefined,
        description: '',
      },
      errors: {},
    };
  },
  watch: {
    // eslint-disable-next-line func-names
    'company.id': function () {
      this.resetForm();
      this.loadForm();
    },
    // eslint-disable-next-line func-names
    'companyProgram.id': function () {
      this.resetForm();
      this.loadForm();
    },
  },
  computed: {
    canSubmit() {
      return form.isValidTextInput(this.form.type)
        && !this.loadingForm
        && this.company.id !== undefined
        && this.companyProgram.id !== undefined;
    },
    SURVEY_TYPES_OPTIONS() {
      return this.$store.state.Surveys.surveyTypes;
    },
    unselectedTypes() {
      return this.SURVEY_TYPES_OPTIONS.filter((type) => !this.types.some((t) => t.types === type.id));
    },
    selectedType() {
      return this.SURVEY_TYPES_OPTIONS.find((type) => type.id === this.form.type);
    },
  },
  methods: {
    resetForm() {
      this.form = {
        type: undefined,
        description: '',
      };
    },
    // eslint-disable-next-line func-names
    loadForm: _debounce(async function () {
      this.loadingForm = true;
      this.errorForm = undefined;

      const surveyTypes = await this.$store.dispatch('getProgramSurveyTypes', [this.companyProgram.id, true]);

      // get survey type details if surveyTypeId is defined
      if (this.surveyTypeId) {
        const surveyType = surveyTypes.find((type) => type.id === this.surveyTypeId);
        this.form.type = surveyType.types;
        this.form.description = surveyType.description;
      }

      if (Array.isArray(surveyTypes)) {
        this.types = surveyTypes;
      }

      await this.$nextTick();

      this.loadingForm = false;
    }, 300),
    async onSubmit() {
      if (!this.canSubmit) {
        this.$toasted.global.appError({
          errorMessage: 'You can\'t submit the form. Check for empty inputs',
        });

        console.warn('Cannot submit form... \n check for empty inputs');
        return false;
      }

      // signup!!
      this.btnDisabled = true;
      this.btnText = 'working...';

      const data = {
        companyid: this.company.id,
        progid: this.companyProgram.id,
        types: this.form.type,
        desc: this.form.description,
        adminid: this.$store.state.User.id,
        suvid: this.surveyId,
        name: this.selectedType?.name,
      };

      const result = await this.$store.dispatch('createProgramSurveyType', data);

      if (result) {
        this.$toasted.success('Saved!', { duration: 1000 });
        this.$emit('created-success');

        if (this.surveyTypeId) {
          this.$store.dispatch('logAction', this.$store.state.Logger.actionsIndexes.editSurvey);
        } else {
          this.$store.dispatch('logAction', this.$store.state.Logger.actionsIndexes.addSurvey);
        }
      } else {
        this.$toasted.global.appError({
          errorMessage: 'Saving survey failed. Please try again later',
        });
      }

      this.btnDisabled = false;
      this.btnText = 'save';

      return true;
    },
  },
  async created() {
    this.errors = { ...this.form };

    await this.$nextTick();
    await this.loadForm();
    await this.$nextTick();

    this.loadingForm = false;
  },
};
</script>

<style>

</style>
